import {
    VideoIcon,
    StarIcon,
    ArrowRightIcon,
    ClockIcon,
    GraduationCapIcon,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { Button } from "@component/button";
import { InterviewItem } from "@type/interview";
import {
    interviewStatusAborted,
    interviewStatusComplete,
    interviewStatusInComplete,
    interviewStatusPartial,
    interviewStatusPendingInterview,
    interviewStatusPendingProcessing,
    interviewStatusPendingVerification
} from "@constant/app";
import { formatDuration, formatHumanReadable } from "@util/date";
import { Badge } from "./badge";
import { Tooltip } from "./tooltip";
import { useEffect, useState } from "react";
import { getInterviewStatus } from "@service/interview";
import { CandidateInterviewStatus } from "@type/candidate";
import SubmittedRetakeDialog from "./submitted-retake-dialog";

interface Props {
    interview: InterviewItem;
    is_practice?: boolean;
    hideDropdown?: boolean;
    onShare?: () => void;
}

export default function InterviewItemCard(props: Props) {
    const navigate = useNavigate();
    const [isLoadingInterviewStatus, setLoadingInterviewStatus] = useState<boolean>(false);
    const [isAlreadyRequestRetakeDialogOpen, setIsAlreadyRequestRetakeDialogOpen] = useState<boolean>(false)
    const [interviewStatus, setInterviewStatus] = useState<CandidateInterviewStatus | undefined>(undefined)

    useEffect(() => {
        if (!props.is_practice) {
            setLoadingInterviewStatus(true);
            getInterviewStatus(props.interview.id).then((resp) => {
                setInterviewStatus(resp.data);
                setLoadingInterviewStatus(false)
            }).catch((error) => {
                console.error('failed to get interview status', error)
                setLoadingInterviewStatus(false)
            })
        }
    }, [props.is_practice])

    const handleRetakeInterview = () => {
        navigate(`/interview/${props.interview.id}/start`, { state: { isPracticeInterview: true } });
    }

    const handleShowConfirmJobInterviewDialog = () => {
        navigate(props.interview.link);
    }

    // Status chip palette — colours mirror getStatusBadgeClass()
    // in @util/string so the chip colours match the rest of the app
    // (interview-assessment page, candidate drawer, etc.):
    //   - Complete                 → green
    //   - Pending* / Interviewer Ready → yellow
    //   - Incomplete / Partial     → orange
    //   - Aborted                  → red
    const getStatusChipClass = (status: string) => {
        switch (status) {
            case interviewStatusPendingVerification:
            case interviewStatusPendingInterview:
            case interviewStatusPendingProcessing:
                return "bg-yellow-100 text-yellow-800 border border-yellow-200";
            case interviewStatusComplete:
                return "bg-green-100 text-green-800 border border-green-200";
            case interviewStatusInComplete:
            case interviewStatusPartial:
                return "bg-orange-100 text-orange-800 border border-orange-200";
            case interviewStatusAborted:
                return "bg-red-100 text-red-800 border border-red-200";
            default:
                return "bg-gray-100 text-gray-800 border border-gray-200";
        }
    };

    const handleClick = () => {
        if (props.is_practice) {
            navigate(`/interview/${props.interview.id}/feedback`);
        } else {
            handleShowConfirmJobInterviewDialog()
        }
    }

    return (
        <>
            <div
                className={`overflow-hidden rounded-xl p-3 select-none shadow-sm cursor-pointer sm:group transition-colors outline outline-1 hover:outline-2 hover:outline-blue-600 ${props.interview.is_premium ? "outline-blue-200" : "outline-gray-200"}`}
                onClick={handleClick}
            >
                <div className="flex flex-col sm:flex-row justify-between sm:gap-0 gap-2">
                    <div className="flex items-center space-x-4">
                        <div className={`${props.interview.is_premium
                            ? "bg-gradient-to-r from-blue-700 to-blue-600"
                            : "bg-blue-100"} p-2 rounded-md`}>
                            {props.interview.is_premium ? (
                                <StarIcon className={`h-5 w-5 ${props.interview.is_premium ? "text-white" : "text-blue-600"}`} />
                            ) : !props.is_practice ? (
                                <VideoIcon className={`h-5 w-5 ${props.interview.is_premium ? "text-white" : "text-blue-600"}`} />
                            ) : (
                                <GraduationCapIcon className={`h-5 w-5 ${props.interview.is_premium ? "text-white" : "text-blue-600"}`} />
                            )}
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900">{props.interview.title}</h3>
                            <div className="flex items-center text-sm text-gray-500 mt-1">
                                <span className="capitalize hidden sm:block">{props.interview.interview_type}</span>

                                {!props.is_practice && (
                                    <div className="hidden sm:block">
                                        <span className="mx-1">•</span>
                                        <span>
                                            {props.interview.company_name}
                                        </span>
                                    </div>
                                )}
                                {props.interview.job_id && (
                                    <div className="hidden sm:block">
                                        <span className="mx-1">•</span>
                                        <span>
                                            {props.interview.job_name}
                                        </span>
                                    </div>
                                )}
                                {props.interview.created_at && props.is_practice ? (
                                    <>
                                        <span className="mx-1">•</span>
                                        <span>Completed on {formatHumanReadable(props.interview.created_at || "", true)}</span>
                                    </>
                                ) : props.interview.created_at && (
                                    <>
                                        <span className="mx-1">•</span>
                                        <span>{formatHumanReadable(props.interview.created_at || "", true)}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <div className="flex items-center transition-all duration-300">
                            {!props.is_practice && (props.interview.duration || 0) > 0 && (
                                <Tooltip
                                    content={`Approximate duration: ${formatDuration(props.interview.duration || 0, "minutes")}`}
                                    side="top"
                                    delayDuration={0}
                                >
                                    <Badge
                                        className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200 mr-2"
                                        style={{ borderRadius: "1rem" }}
                                    >
                                        <ClockIcon className="inline h-3 w-3 mr-1" />
                                        <span>{formatDuration(props.interview.duration || 0)}</span>
                                    </Badge>
                                </Tooltip>
                            )}

                            {props.is_practice ? (
                                <div
                                    className={`px-3 -mr-6 transition-all duration-300 group-hover:mr-2 py-1 rounded-full text-xs font-medium ${getStatusChipClass(props.interview.status || "")}`}
                                >
                                    {props.interview.status === interviewStatusComplete ? "View feedback" : "Processing feedback"}
                                </div>
                            ) : isLoadingInterviewStatus && !interviewStatus ? (
                                <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <div
                                    className={`px-3 ${(
                                        interviewStatus?.status === interviewStatusComplete
                                        || interviewStatus?.status === interviewStatusAborted
                                        || interviewStatus?.status === interviewStatusPartial
                                        || interviewStatus?.status === interviewStatusInComplete
                                    ) ? "-mr-24" : "-mr-28"} transition-all duration-300 group-hover:mr-4 py-1 rounded-full text-xs font-medium ${getStatusChipClass(interviewStatus?.status || "")}`}
                                >
                                    {interviewStatus?.status === interviewStatusComplete
                                        ? "Completed"
                                        : interviewStatus?.status === "Not Started"
                                            ? interviewStatus?.status
                                            : interviewStatus?.status === interviewStatusInComplete
                                                ? "Incomplete"
                                                : interviewStatus?.status === interviewStatusAborted
                                                    ? "Aborted"
                                                    : interviewStatus?.status === interviewStatusPartial
                                                        ? "Partial"
                                                        : "Pending"}
                                </div>
                            )}
                            {!isLoadingInterviewStatus && (
                                <button
                                    className="flex items-center text-blue-600 hover:text-blue-800 text-xs opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                                    onClick={handleClick}
                                >
                                    {!props.is_practice && (
                                        <span className="mr-1">
                                            {(
                                                interviewStatus?.status === interviewStatusComplete
                                                || interviewStatus?.status === interviewStatusAborted
                                                || interviewStatus?.status === interviewStatusPartial
                                                || interviewStatus?.status === interviewStatusInComplete
                                            )
                                                ? "View interview"
                                                : "Attempt interview"}
                                        </span>
                                    )}
                                    <ArrowRightIcon className={`h-4 w-4 ${props.is_practice ? props.interview.status === interviewStatusComplete ? "text-green-600" : "text-yellow-600" : ""}`} />
                                </button>
                            )}
                        </div>

                        {
                            props.hideDropdown && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            type="button"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="h-4 w-4"
                                            >
                                                <circle cx="12" cy="12" r="1" />
                                                <circle cx="19" cy="12" r="1" />
                                                <circle cx="5" cy="12" r="1" />
                                            </svg>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <DropdownMenuItem onClick={handleRetakeInterview}>
                                            <VideoIcon className="h-4 w-4 mr-2" />
                                            <span>Retake Interview</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )
                        }
                    </div>
                </div>
            </div>

            <SubmittedRetakeDialog
                isOpen={isAlreadyRequestRetakeDialogOpen}
                onClose={() => { setIsAlreadyRequestRetakeDialogOpen(false) }}
            />
        </>
    );
}